import { useEffect, useState } from 'react';
import { Copy, CreditCard, ExternalLink, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';
import { useEstimateDepositLink, useUpdateEstimate } from '@/hooks/useEstimates';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import type { EstimateWithRelations } from '@/types/estimate';

const EDITABLE = new Set(['draft', 'sent', 'viewed']);

/**
 * Deposit asked when the customer accepts online (migration 20260924170000).
 * Before acceptance: the % is editable. After: what is owed, what Square has taken,
 * and the pay link to send the customer.
 */
export function EstimateDepositPanel({ estimate }: { estimate: EstimateWithRelations }) {
  const { formatMoney, formatShortDate } = useCompanyLocale();
  const updateEstimate = useUpdateEstimate();
  const depositLink = useEstimateDepositLink();

  const percent = Number(estimate.deposit_percent ?? 0);
  const [draft, setDraft] = useState(String(percent));
  useEffect(() => setDraft(String(Number(estimate.deposit_percent ?? 0))), [estimate.deposit_percent]);

  const status = estimate.status;
  if (status === 'declined' || status === 'cancelled' || status === 'expired') return null;

  if (EDITABLE.has(status)) {
    const preview = Math.round((Number(estimate.estimate_amount) || 0) * percent) / 100;
    const save = () => {
      const next = Math.min(100, Math.max(0, Number(draft) || 0));
      if (next === percent) { setDraft(String(percent)); return; }
      updateEstimate.mutate(
        { id: estimate.id, deposit_percent: next || null },
        {
          onSuccess: () => toast({ title: next ? `Deposit set to ${next}%` : 'No deposit on this quote' }),
          onError: (e) => toast({ title: 'Could not save the deposit', description: e instanceof Error ? e.message : undefined, variant: 'destructive' }),
        },
      );
    };
    return (
      <div className="flex flex-wrap items-center gap-2 border-t pt-3 text-sm">
        <CreditCard className="h-4 w-4 text-muted-foreground" />
        <span className="text-muted-foreground">Deposit on acceptance</span>
        <div className="ml-auto flex items-center gap-2">
          <Input
            type="number"
            min={0}
            max={100}
            step={1}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            className="h-8 w-20 text-right"
            aria-label="Deposit percent"
            disabled={updateEstimate.isPending}
          />
          <span className="text-muted-foreground">%</span>
          <span className="w-24 text-right tabular-nums">{percent > 0 ? formatMoney(preview) : 'None'}</span>
        </div>
      </div>
    );
  }

  // Accepted
  const amount = Number(estimate.deposit_amount ?? 0);
  if (!(amount > 0)) {
    return (
      <div className="flex items-center gap-2 border-t pt-3 text-sm text-muted-foreground">
        <CreditCard className="h-4 w-4" />
        No deposit was asked on this quote.
      </div>
    );
  }
  const paid = Number(estimate.deposit_paid_amount ?? 0);
  const fullyPaid = paid >= amount - 0.004;
  const link = !fullyPaid ? estimate.deposit_payment_url ?? depositLink.data?.payment_url ?? null : null;

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Payment link copied', description: 'Send it to the customer by text or email.' });
    } catch {
      toast({ title: 'Copy failed', description: url, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-2 border-t pt-3 text-sm">
      <div className="flex items-center gap-2">
        <CreditCard className="h-4 w-4 text-muted-foreground" />
        <span className="text-muted-foreground">
          Deposit{estimate.deposit_percent ? ` (${Number(estimate.deposit_percent)}%)` : ''}
        </span>
        <span className="ml-auto tabular-nums font-medium">{formatMoney(amount)}</span>
        {fullyPaid ? (
          <Badge variant="success">Paid{estimate.deposit_paid_at ? ` ${formatShortDate(estimate.deposit_paid_at)}` : ''}</Badge>
        ) : paid > 0 ? (
          <Badge variant="warning">{formatMoney(paid)} paid</Badge>
        ) : (
          <Badge variant="warning">Unpaid</Badge>
        )}
      </div>
      {fullyPaid && estimate.deposit_job_id == null && (
        <p className="text-xs text-muted-foreground">It will be added to the job's payments when the job is booked.</p>
      )}
      {!fullyPaid && (
        <div className="flex flex-wrap gap-2">
          {link ? (
            <>
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => copy(link)}>
                <Copy className="h-3.5 w-3.5" /> Copy pay link
              </Button>
              <Button size="sm" variant="ghost" className="gap-1.5" asChild>
                <a href={link} target="_blank" rel="noreferrer"><ExternalLink className="h-3.5 w-3.5" /> Open</a>
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              disabled={depositLink.isPending}
              onClick={() =>
                depositLink.mutate(estimate.id, {
                  onSuccess: (d) => d.payment_url && copy(d.payment_url),
                  onError: (e) => toast({ title: 'No payment link', description: e instanceof Error ? e.message : undefined, variant: 'destructive' }),
                })
              }
            >
              {depositLink.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CreditCard className="h-3.5 w-3.5" />}
              Create pay link
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
